import { FilterButtonStyled } from '@components/Filter';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MouseEvent } from 'react';

import { useSearchPresetContext } from './SearchPresetContext';

export const PresetChooseButton = () => {
  const { openChooseMenu } = useSearchPresetContext();

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    openChooseMenu(event.currentTarget);
  };

  return (
    <FilterButtonStyled onClick={handleOpenMenu}>
      <i className="fi-br-bookmark" style={{ fontSize: 14 }} />
    </FilterButtonStyled>
  );
};

export const PresetSaveButton = () => {
  const { openSaveDialog } = useSearchPresetContext();

  return (
    <FilterButtonStyled onClick={openSaveDialog}>
      <i className="fi-br-disk" style={{ fontSize: 18 }} />
    </FilterButtonStyled>
  );
};

export const PresetMenu = () => {
  const {
    presets,
    personalPresets,
    sharedPresets,
    anchorEl,
    closeChooseMenu,
    applyPreset,
    deletePresetById,
  } = useSearchPresetContext();

  const handleDelete = async (id: string, event: MouseEvent) => {
    event.stopPropagation();
    await deletePresetById(id);
  };

  return (
    <Dialog open={Boolean(anchorEl)} onClose={closeChooseMenu} maxWidth="xs" fullWidth>
      <DialogTitle>Choisir un préréglage</DialogTitle>
      <DialogContent dividers>
        {presets.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
            Aucun préréglage
          </Typography>
        )}

        {personalPresets.length > 0 && (
          <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
            Mes préréglages
          </Typography>
        )}
        <List disablePadding>
          {personalPresets.map((preset) => (
            <ListItem
              key={preset._id}
              disablePadding
              secondaryAction={
                <IconButton size="small" onClick={(event) => handleDelete(preset._id, event)}>
                  <i className="fi-br-trash" style={{ fontSize: 12 }} />
                </IconButton>
              }
            >
              <ListItemButton onClick={() => applyPreset(preset)}>
                <ListItemText primary={preset.name} secondary={preset.description} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {sharedPresets.length > 0 && <Divider />}
        {sharedPresets.length > 0 && (
          <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', display: 'block' }}>
            Préréglages partagés
          </Typography>
        )}
        <List disablePadding>
          {sharedPresets.map((preset) => (
            <ListItem
              key={preset._id}
              disablePadding
              secondaryAction={<Chip label="Partagé" size="small" variant="outlined" sx={{ ml: 1 }} />}
            >
              <ListItemButton onClick={() => applyPreset(preset)}>
                <ListItemText primary={preset.name} secondary={preset.description} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeChooseMenu}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export const PresetSaveDialog = () => {
  const {
    creating,
    saveDialogOpen,
    closeSaveDialog,
    presetName,
    presetDescription,
    isShared,
    setPresetName,
    setPresetDescription,
    setIsShared,
    saveCurrentPreset,
  } = useSearchPresetContext();

  return (
    <Dialog open={saveDialogOpen} onClose={closeSaveDialog} maxWidth="xs" fullWidth>
      <DialogTitle>Enregistrer le préréglage</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label="Nom"
          fullWidth
          value={presetName}
          onChange={(event) => setPresetName(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          label="Description (optionnel)"
          fullWidth
          multiline
          rows={2}
          value={presetDescription}
          onChange={(event) => setPresetDescription(event.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={<Switch checked={isShared} onChange={(event) => setIsShared(event.target.checked)} />}
          label="Partager avec l'équipe"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeSaveDialog} variant="text" color="secondary">Annuler</Button>
        <Button
          variant="contained"
          onClick={saveCurrentPreset}
          disabled={creating || !presetName.trim()}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};
